// documentation https://oneiig.sharepoint.com/:w:/r/sites/StorageOne53/_layouts/15/doc2.aspx?sourcedoc=%7B01055759-a459-4793-8bfd-56f9f7ddffa1%7D&action=edit&wdPid=381aeabd
import { ComponentIds, HierarchyUtils, Vector } from '@inter-ikea-core/core';
import { ErrorMessages, PlannerEcsSystem, ProductType, normalizeDirection } from '@inter-ikea-storageone/range';
import AutofillCornerComponent from '../../components/AutofillCornerComponent';
import { getAutofillRecommendationFromDataSet } from './RuleSet/AutofillRuleSet';
import { CustomComponentIds } from '@inter-ikea-kvadrat/kvadrat';
/**
 * This system builds a corner solution in the closest corner to where the camera is aimed.
 */
export class AutofillCornerSystem extends PlannerEcsSystem {
    /**
     * Creates an instance of AutofillCornerSystem.
     * @param {CoreApi} coreApi - The core API instance.
     */
    constructor(coreApi) {
        super(coreApi, 'AutofillCornerSystem', AutofillCornerComponent.id);
        /**
         * Finds a corner unit in all products.
         * @param {AutofillCornerComponent} component - The component.
         * @param {Product[]} allCornerUnits - All corner units.
         * @param {boolean} mirrored - Whether the unit is mirrored.
         * @param {number} mainFrameWidth - The main frame width.
         * @param {number} mainFrameDepth - The main frame depth.
         * @param {number} addonUnitDepth - The addon unit depth.
         * @returns {Product | undefined} - The corner unit product.
         */
        this.findCornerUnitInAllProducts = (component, allCornerUnits, mirrored, mainFrameWidth, mainFrameDepth, addonUnitDepth) => {
            const cornerUnits = [
                ...allCornerUnits.filter(p => p.type === ProductType.PAX_CORNER_SOLUTION_ASM)
            ];
            const cornerUnit = cornerUnits.find(p => p.parts.some(part => part.product?.communicatedDimensions.depth === addonUnitDepth &&
                part.product?.type === ProductType.PAX_ADD_ON_CORNER_FRAME_ASM) &&
                p.parts.some(part => part.product?.communicatedDimensions.depth === mainFrameDepth &&
                    part.product.communicatedDimensions.width === mainFrameWidth &&
                    part.product?.type === ProductType.PAX_FRAME) &&
                p.design === component.design &&
                p.communicatedDimensions.height === component.frameHeight &&
                (mirrored ? p.coreProductId.includes('mirrored') : !p.coreProductId.includes('mirrored')));
            return cornerUnit;
        };
        this.coreApi = coreApi;
        this.frameProducts = [];
    }
    /**
     * Sets the market products for the system.
     * @param {Product[]} marketProducts - The market products.
     */
    setMarketProducts(marketProducts) {
        this.frameProducts = marketProducts.filter(p => p.type === ProductType.PAX_FRAME || p.type === ProductType.PAX_CORNER_SOLUTION_ASM);
    }
    /**
     * Called when a component is added to an entity.
     * @param {Entity} entity - The entity.
     * @param {AutofillCornerComponent} component - The component.
     */
    onAdd(entity, component) {
        this.fillScene(component);
    }
    /**
     * Called when a component is updated on an entity.
     * @param {Entity} entity - The entity.
     * @param {AutofillCornerComponent} oldComponent - The old component.
     * @param {AutofillCornerComponent} newComponent - The new component.
     */
    onUpdate(entity, oldComponent, newComponent) {
        this.fillScene(newComponent);
    }
    /**
     * Called when a component is removed from an entity.
     */
    onRemove() { }
    /**
     * Fills the scene with the corner solution.
     * @param {AutofillCornerComponent} component - The component.
     */
    fillScene(component) {
        const corner = this.getClosestCorner();
        if (corner) {
            this.coreApi.history.historyAction(() => {
                this.addFramesToScene(this.coreApi, corner.position, component);
            }, true);
        }
    }
    /**
     * Gets all floor corner entities from the scene.
     * @returns {Corner[]} Array of corners with their positions and forward directions.
     */
    getFloorCornerEntities() {
        const scene = this.coreApi.entities.getScene();
        const room = scene?.entityManager.entities.find(e => e.hasComponent(CustomComponentIds.ROOM_COMPONENT_ID));
        const floorCornerEntities = room?.entityManager.entities.filter(e => e.hasComponent(CustomComponentIds.ROOM_CORNER_COMPONENT_ID));
        const corners = [];
        floorCornerEntities?.forEach(e => {
            const worldTransform = e.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)?.transform;
            const conns = e
                .getComponent(ComponentIds.CONNECTORS_COMPONENT_ID)
                ?.connectors.filter(c => c.offers().includes('wall-corner'));
            conns?.forEach(c => {
                corners.push({
                    position: c.getWorldPositionFromOwnerEntity(e),
                    forward: worldTransform.forward.unaryMinus()
                });
            });
        });
        return corners;
    }
    /**
     * Gets all floor corner connectors from the given corner entities.
     * @param {Entity[]} corners - Array of corner entities.
     * @returns {PointConnector[]} Array of point connectors.
     */
    getFloorCornerConnectors(corners) {
        const connectors = [];
        corners.forEach(c => {
            const conns = c.getComponent(ComponentIds.CONNECTORS_COMPONENT_ID)
                ?.connectors;
            if (conns) {
                connectors.push(...conns.filter(c => c.offers().includes('wall-corner')));
            }
        });
        return connectors;
    }
    /**
     * Finds the corner closest to where the camera is looking.
     * @returns {Corner | undefined} The closest corner or undefined if none found.
     */
    getClosestCorner() {
        const corners = this.getFloorCornerEntities();
        const cameraDirectionNormalV3 = this.coreApi.camera.getActiveCamera().forward;
        const cameraDirectionNormal = new Vector(cameraDirectionNormalV3.x, cameraDirectionNormalV3.y, cameraDirectionNormalV3.z);
        let placementMostlyFacingCamera = { p: corners[0], diff: 1 };
        corners.forEach(p => {
            // Calculate the absolute difference between the wall rotation and the camera
            const diff = p.forward.dot(cameraDirectionNormal);
            if (diff < placementMostlyFacingCamera.diff) {
                placementMostlyFacingCamera = { p, diff };
            }
        });
        return placementMostlyFacingCamera.p;
    }
    /**
     * Adds frames to the scene.
     * @param {CoreApi} coreApi - The core API instance.
     * @param {Vector} position - The position to add the frames.
     * @param {AutofillCornerComponent} component - The component.
     */
    addFramesToScene(coreApi, position, component) {
        if (component.leftWallWidth === 0 || component.rightWallWidth === 0) {
            return;
        }
        const cornerEntity = this.addCornerUnit(coreApi, position, component);
        if (!cornerEntity) {
            return;
        }
        const { mainFrame, addonFrame } = this.getCornerSolutionFrames(cornerEntity);
        if (mainFrame && addonFrame) {
            const addonToTheRight = this.getAddonPosition(cornerEntity);
            this.addFramesToSide(mainFrame, addonFrame, true, component, addonToTheRight);
            this.addFramesToSide(mainFrame, addonFrame, false, component, addonToTheRight);
        }
        component.finished();
    }
    /**
     * Adds frames to a side of the corner solution.
     * @param {Entity} mainFrame - The main frame entity.
     * @param {Entity} addonFrame - The addon frame entity.
     * @param {boolean} isRightSide - Whether it is the right side.
     * @param {AutofillCornerComponent} component - The component.
     * @param {boolean} addonToTheRight - Whether the addon is to the right.
     */
    addFramesToSide(mainFrame, addonFrame, isRightSide, component, addonToTheRight) {
        const mainFrameSize = mainFrame.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size;
        const addonFrameSize = addonFrame.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size;
        let startEntity = mainFrame;
        let widthToSubtract = 0;
        if ((isRightSide && addonToTheRight) || (!isRightSide && !addonToTheRight)) {
            startEntity = addonFrame;
        }
        if (mainFrameSize && addonFrameSize) {
            if (isRightSide) {
                widthToSubtract = addonToTheRight
                    ? mainFrameSize.depth + addonFrameSize.width
                    : mainFrameSize.width + 110;
            }
            else {
                widthToSubtract = addonToTheRight
                    ? mainFrameSize.width + 110
                    : mainFrameSize.depth + addonFrameSize.width;
            }
            const wallWidth = (isRightSide ? component.rightWallWidth : component.leftWallWidth) - widthToSubtract;
            const recommendations = getAutofillRecommendationFromDataSet(this.frameProducts, isRightSide ? component.rightWallDepth : component.leftWallDepth, component.design, component.frameHeight, false, wallWidth);
            if (recommendations?.length > 0) {
                let prev = startEntity;
                for (let i = 0; i < recommendations.length; i++) {
                    const result = this.addFrame(prev, recommendations[i].frame.coreProductId);
                    if (result && result.valid) {
                        prev = result.placedEntity;
                    }
                    else if (result && !result.valid) {
                        // eslint-disable-next-line no-console
                        console.log('could not add frame', result);
                        return;
                    }
                }
            }
        }
    }
    /**
     * Adds a corner unit to the scene.
     * @param {CoreApi} coreApi - The core API instance.
     * @param {Vector} position - The position to add the corner unit.
     * @param {AutofillCornerComponent} component - The component.
     * @returns {Entity | undefined} - The corner unit entity.
     */
    addCornerUnit(coreApi, position, component) {
        const { mirrored, mainFrameWidth, mainFrameDepth, addonFrameDepth } = this.calcCornerUnitFilter(component);
        const cornerUnit = this.findCornerUnitInAllProducts(component, this.frameProducts, mirrored, mainFrameWidth, mainFrameDepth, addonFrameDepth);
        if (cornerUnit) {
            coreApi.entities.getAll().forEach(e => coreApi.entities.delete(e));
            const cornerEntity = this.coreApi
                .getServices()
                .productManager.createEntity(cornerUnit.coreProductId);
            if (cornerEntity) {
                this.coreApi.placementService.snapToPosition(cornerEntity, position, true);
                return cornerEntity;
            }
        }
        else {
            coreApi.emit.onPlacementAttempt(ErrorMessages.ChangeColorMissingFramesMessage);
        }
    }
    /**
     * Calculates the filter for the corner unit.
     * @param {AutofillCornerComponent} component - The component.
     * @returns {object} - The filter parameters.
     */
    calcCornerUnitFilter(component) {
        // Mirrored = Addon unit to the right / Main frame to the left
        // Not mirrored = Main frame to the right / Addon unit to the left
        let mirrored = false;
        let mainFrameWidth = 0;
        let mainFrameDepth = 0;
        let addonFrameDepth = 0;
        if (component.leftWallDepth === 580 && component.rightWallDepth === 580) {
            if ((component.leftWallWidth >= 630 && component.leftWallWidth < 1110) ||
                (component.leftWallWidth >= 1360 && component.leftWallWidth < 1610)) {
                mainFrameWidth = 750;
                mirrored = true;
            }
            else {
                mainFrameWidth =
                    component.rightWallWidth === 860 || component.rightWallWidth === 1360 ? 750 : 1000;
            }
        }
        else if (component.leftWallDepth === 350 && component.rightWallDepth === 350) {
            mainFrameWidth = 750;
            if ((component.leftWallWidth >= 630 && component.leftWallWidth < 880) ||
                (component.leftWallWidth >= 1130 && component.leftWallWidth < 1380)) {
                mirrored = true;
            }
            if (component.leftWallWidth === 1130 ||
                component.rightWallWidth === 1130 ||
                (component.leftWallWidth === 1630 && component.rightWallWidth === 1130) ||
                (component.leftWallWidth === 880 && component.rightWallWidth === 1130) ||
                ((component.leftWallWidth === 1380 || component.leftWallWidth === 880) &&
                    component.rightWallWidth === 1130)) {
                mainFrameWidth = 1000;
            }
        }
        else if (component.leftWallDepth === 350 && component.rightWallDepth === 580) {
            mainFrameWidth = 750;
            mirrored = false;
            if ([860, 1360].includes(component.leftWallWidth)) {
                mirrored = true;
            }
            if (component.leftWallWidth === 1360 && component.rightWallWidth === 1130) {
                mirrored = false;
            }
            if (component.rightWallWidth === 1130) {
                mainFrameWidth = 1000;
            }
        }
        else if (component.leftWallDepth === 580 && component.rightWallDepth === 350) {
            mainFrameWidth = 750;
            mirrored = true;
            if ((component.leftWallWidth !== 1130 &&
                component.leftWallWidth !== 1860 &&
                component.leftWallWidth < 1630 &&
                [860, 1360, 1860].includes(component.rightWallWidth)) ||
                (component.leftWallWidth === 1630 && [860, 1360].includes(component.rightWallWidth))) {
                mirrored = false;
            }
            if ((component.leftWallWidth === 880 && component.rightWallWidth === 1860) ||
                component.leftWallWidth === 1130) {
                mainFrameWidth = 1000;
            }
        }
        mainFrameDepth = mirrored ? component.leftWallDepth : component.rightWallDepth;
        addonFrameDepth = mirrored ? component.rightWallDepth : component.leftWallDepth;
        return {
            mirrored,
            mainFrameWidth,
            mainFrameDepth,
            addonFrameDepth
        };
    }
    /**
     * Gets the position of the addon frame relative to the main frame.
     * @param {Entity} cornerEntity - The corner entity.
     * @returns {boolean} - Whether the addon is to the right.
     */
    getAddonPosition(cornerEntity) {
        const { mainFrame, addonFrame } = this.getCornerSolutionFrames(cornerEntity);
        if (addonFrame && mainFrame) {
            const addonWorldTransform = addonFrame.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)?.transform;
            const mainFrameWorldTransform = mainFrame.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)?.transform;
            let degreeDifference = normalizeDirection(addonWorldTransform.rotation.toEulerDegrees().Y) -
                normalizeDirection(mainFrameWorldTransform.rotation.toEulerDegrees().Y);
            degreeDifference = ((degreeDifference + 180) % 360) - 180;
            return degreeDifference < 0;
        }
        return false;
    }
    /**
     * Adds a new frame to the host entity by aligning it.
     * @param {Entity} hostEntity - The host entity to align to.
     * @param {string} frameToAddCoreProductId - Product ID of the frame to add.
     * @returns {Object | undefined} Result of the placement operation.
     */
    addFrame(hostEntity, frameToAddCoreProductId) {
        const newEntity = this.coreApi
            .getServices()
            .productManager.createEntity(frameToAddCoreProductId);
        if (newEntity) {
            return this.coreApi.placementService.alignToTarget(newEntity, hostEntity);
        }
    }
    /**
     * Gets the frames that make up a corner solution.
     * @param {Entity} entity - The parent corner solution entity.
     * @returns {Object} Object containing the addon frame, main frame and measurement frame.
     */
    getCornerSolutionFrames(entity) {
        const addonFrame = HierarchyUtils.descendants(entity).find(it => this.coreApi.entities.isProductType(it, ProductType.PAX_ADD_ON_CORNER_FRAME));
        const mainFrame = HierarchyUtils.descendants(entity).find(it => this.coreApi.entities.isProductType(it, ProductType.PAX_FRAME));
        const measurementFrame = HierarchyUtils.descendants(entity).find(it => this.coreApi.entities.isProductType(it, ProductType.PAX_CORNER_MEASUREMENT_FRAME));
        return { addonFrame, mainFrame, measurementFrame };
    }
    /**
     * Normalizes an angle to be within [0, 360) degrees.
     * @param {number} direction - Angle in degrees to normalize.
     * @returns {number} Normalized angle in degrees.
     */
    normalizeDirection(direction) {
        // Normalize direction to be within [0, 360) range
        direction %= 360;
        if (direction < 0) {
            direction += 360;
        }
        return direction;
    }
}
//# sourceMappingURL=AutofillCornerSystem.js.map